import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { SelectButton } from "primereact/selectbutton";
import { useEffect, useState } from "react";
import * as client from "../../../client";
import { apiErrorDetail } from "../../../api/errors";
import { useToast } from "../../../context-providers/Toast";
import { EnrichmentBadge } from "../cat/EnrichmentBadge";
import {
  ENRICHMENT_SOURCE_OPTIONS,
  ENRICHMENT_VENDOR_LABELS,
  EnrichmentChoice,
  EnrichmentVendor,
  enrichmentSourceLabel,
  fromEnrichmentChoice,
  toEnrichmentChoice,
} from "../cat/types";
import { useSetCatVendorEnrichment } from "../data";
import styles from "./VendorEnrichmentDialog.module.css";

type Props = {
  reportId: string;
  state: client.VendorEnrichmentState | undefined;
  visible: boolean;
  onHide: () => void;
};

// Picks, per vendor, which of the report's completed runs is live, by the
// vendor data it modeled with. Nothing is re-modeled: saving promotes the
// stored run. Vendors with no completed run at all are left out.
export const VendorEnrichmentDialog = ({
  reportId,
  state,
  visible,
  onHide,
}: Props) => {
  const toast = useToast();
  const setVendorEnrichment = useSetCatVendorEnrichment(reportId);
  const vendors = (state?.vendors ?? []).filter(
    (v) => v.has_live_run || v.options.some((o) => o.available)
  );
  const [choices, setChoices] = useState<
    Partial<Record<EnrichmentVendor, EnrichmentChoice>>
  >({});
  // Opening always starts from what is live.
  useEffect(() => {
    if (visible) setChoices({});
  }, [visible]);

  const liveChoice = (v: client.VendorEnrichment): EnrichmentChoice | null =>
    v.has_live_run ? toEnrichmentChoice(v.live_source) : null;
  const chosen = (v: client.VendorEnrichment): EnrichmentChoice | null =>
    choices[v.vendor] ?? liveChoice(v);
  const changes = vendors.flatMap((v) => {
    const choice = choices[v.vendor];
    return choice !== undefined && choice !== liveChoice(v)
      ? [{ vendor: v, from: liveChoice(v), to: choice }]
      : [];
  });

  const save = () =>
    setVendorEnrichment.mutate(
      changes.map((c) => ({
        vendor: c.vendor.vendor,
        source: fromEnrichmentChoice(c.to),
      })),
      {
        onSuccess: () => {
          toast.show({
            severity: "success",
            summary: "Vendor data switched",
            detail: `${changes.length} vendor${changes.length === 1 ? "" : "s"} now price from the chosen runs.`,
          });
          onHide();
        },
        onError: (error) =>
          toast.show({
            severity: "error",
            summary: "Vendor data not switched",
            detail: apiErrorDetail(error),
          }),
      }
    );

  return (
    <Dialog
      header="Vendor data"
      visible={visible}
      onHide={onHide}
      className={styles.dialog}
      footer={
        <div className={styles.footer}>
          <div className={styles.changes}>
            {changes.length === 0 ? (
              <span className={styles.noChanges}>No changes</span>
            ) : (
              changes.map((c) => (
                <span key={c.vendor.vendor} className={styles.change}>
                  <span className={styles.changeVendor}>
                    {ENRICHMENT_VENDOR_LABELS[c.vendor.vendor]}
                  </span>
                  {c.from ? (
                    <EnrichmentBadge source={fromEnrichmentChoice(c.from)} />
                  ) : (
                    <span className={styles.noRun}>no live run</span>
                  )}
                  <i className="pi pi-arrow-right" />
                  <EnrichmentBadge source={fromEnrichmentChoice(c.to)} />
                </span>
              ))
            )}
          </div>
          <div className={styles.actions}>
            <Button
              label="Cancel"
              text
              onClick={onHide}
              disabled={setVendorEnrichment.isPending}
            />
            <Button
              label="Save"
              icon="pi pi-check"
              onClick={save}
              disabled={changes.length === 0}
              loading={setVendorEnrichment.isPending}
            />
          </div>
        </div>
      }
    >
      <p className={styles.intro}>
        Each vendor prices from one completed run. Pick the vendor data that run
        modeled with; saving switches the vendor to the run it already holds and
        re-prices the layers. Nothing is re-modeled.
      </p>
      {vendors.length === 0 ? (
        <p className={styles.empty}>No completed modeling runs yet.</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Vendor</th>
              <th>Live</th>
              <th>Price from</th>
            </tr>
          </thead>
          <tbody>
            {vendors.map((v) => {
              const available = new Set(
                v.options
                  .filter((o) => o.available)
                  .map((o) => toEnrichmentChoice(o.source))
              );
              const changed =
                choices[v.vendor] !== undefined &&
                choices[v.vendor] !== liveChoice(v);
              return (
                <tr
                  key={v.vendor}
                  className={changed ? styles.changedRow : undefined}
                >
                  <td className={styles.vendor}>
                    {ENRICHMENT_VENDOR_LABELS[v.vendor]}
                  </td>
                  <td>
                    {v.has_live_run ? (
                      <EnrichmentBadge source={v.live_source} />
                    ) : (
                      <span className={styles.noRun}>none</span>
                    )}
                  </td>
                  <td>
                    <SelectButton
                      value={chosen(v)}
                      options={ENRICHMENT_SOURCE_OPTIONS.map((o) => ({
                        ...o,
                        title: available.has(o.value)
                          ? `Price ${ENRICHMENT_VENDOR_LABELS[v.vendor]} from its newest ${o.label} run`
                          : `No completed ${ENRICHMENT_VENDOR_LABELS[v.vendor]} run with ${o.label}. Run modeling with it from the Modeling tab.`,
                      }))}
                      optionLabel="label"
                      optionValue="value"
                      optionDisabled={(o) => !available.has(o.value)}
                      allowEmpty={false}
                      onChange={(e) => {
                        if (e.value == null) return;
                        setChoices((prev) => ({
                          ...prev,
                          [v.vendor]: e.value,
                        }));
                      }}
                      itemTemplate={(o: { label: string; title: string }) => (
                        <span title={o.title}>{o.label}</span>
                      )}
                      className={styles.picker}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <p className={styles.footnote}>
        Greyed-out choices have no completed run on this report under that
        vendor data (
        {ENRICHMENT_SOURCE_OPTIONS.map((o) =>
          enrichmentSourceLabel(fromEnrichmentChoice(o.value))
        ).join(", ")}
        ). Engineers&apos; test runs are never offered.
      </p>
    </Dialog>
  );
};
